import type { Household } from 'src/types/snugtotal';

/**
 * Helper: Check if any document in array has approved status
 * Documents are considered approved if status is APPROVED, ANALYSIS_PENDING, or ANALYSIS_COMPLETE
 */
const hasApproved = (docs?: any[]): boolean => {
  return docs?.some((d) =>
    d?.status === 'APPROVED' ||
    d?.status === 'ANALYSIS_PENDING' ||
    d?.status === 'ANALYSIS_COMPLETE'
  ) ?? false;
};

/**
 * Check if all checklist documents are completed for a household.
 *
 * This function determines if a household with an active or trialing subscription
 * has completed all required documents to access the premium dashboard.
 *
 * Returns true if:
 * - All enabled document types (Will/Trust, FPOA, HCD) have approved documents
 * - Disabled document types are skipped in the check
 *
 * @param household - The household object to check
 * @returns boolean - true if all required documents are complete
 */
export const areChecklistDocumentsComplete = (household: Household | null): boolean => {
  if (!household || !household.head_of_household) {
    return false;
  }

  const hoh = household.head_of_household;
  const members = household.household_member_roles ?? [];
  const spouse = members.find(m => m.role === 'SPOUSE')?.role_target_user_data ?? null;

  // Get household-level allow flags - these determine which documents are required
  const householdAllowsFpoa = household.household_role?.checklist_display_fpoa ?? true;
  const householdAllowsHcd = household.household_role?.checklist_display_hcd ?? true;
  const householdAllowsWillsTrusts = household.household_role?.checklist_display_willstrusts ?? true;
  const householdAllowsAssets = household.household_role?.checklist_display_assets ?? true;

  const epf = hoh.estate_plan_foundation as
    | 'joint' | 'trust' | 'will' | 'attorney' | 'skip' | '' | null;

  // Check Will/Trust documents (if enabled)
  if (householdAllowsWillsTrusts) {
    // If estate plan foundation is not set or is 'skip', not complete
    if (epf === 'skip' || epf === '' || epf === null) {
      return false;
    }

    // Check if HOH has approved will/trust documents
    if (!hasApproved(hoh.wills_and_trusts)) {
      return false;
    }

    // For 'trust' or 'will' foundation (not joint or attorney), check individual member documents
    if (epf === 'trust' || epf === 'will') {
      for (const member of members) {
        if (member.role_target_user_data?.wills_and_trusts) {
          // If member has documents, they must be approved
          if (member.role_target_user_data.wills_and_trusts.length > 0) {
            if (!hasApproved(member.role_target_user_data.wills_and_trusts)) {
              return false;
            }
          }
        }
      }
    }
  }

  // Check FPOA documents (if enabled)
  if (householdAllowsFpoa) {
    // Check HOH FPOA
    const hohFpoaDocs = hoh.financial_powers_of_attorney ?? [];
    if (!hasApproved(hohFpoaDocs) && (hoh.include_fpoa ?? true)) {
      return false;
    }

    // Check spouse FPOA if they opted in
    if (spouse) {
      const spouseFpoaDocs = spouse.financial_powers_of_attorney ?? [];
      if (!hasApproved(spouseFpoaDocs) && (spouse.include_fpoa ?? true)) {
        return false;
      }
    }

    // Check other household members
    for (const member of members) {
      if (member.role !== 'SPOUSE' && member.role_target_user_data) {
        const memberFpoaDocs = member.role_target_user_data.financial_powers_of_attorney ?? [];
        if (!hasApproved(memberFpoaDocs) && (member.role_target_user_data.include_fpoa ?? true)) {
          return false;
        }
      }
    }
  }

  // Check HCD documents (if enabled)
  if (householdAllowsHcd) {
    // Check HOH HCD
    const hohHcdDocs = hoh.healthcare_directives ?? [];
    if (!hasApproved(hohHcdDocs) && (hoh.include_hcd ?? true)) {
      return false;
    }

    // Check spouse HCD if they opted in
    if (spouse) {
      const spouseHcdDocs = spouse.healthcare_directives ?? [];
      if (!hasApproved(spouseHcdDocs) && (spouse.include_hcd ?? true)) {
        return false;
      }
    }

    // Check other household members
    for (const member of members) {
      if (member.role !== 'SPOUSE' && member.role_target_user_data) {
        const memberHcdDocs = member.role_target_user_data.healthcare_directives ?? [];
        if (!hasApproved(memberHcdDocs) && (member.role_target_user_data.include_hcd ?? true)) {
          return false;
        }
      }
    }
  }

  // Check Asset Planning (if enabled)
  // TODO: This check requires assets to be added as an expandable field on UserDataObj
  // For now, this section is a placeholder for when assets are included in the API response
  if (householdAllowsAssets) {
    // Check HOH assets
    // Note: assets field would need to be added to UserDataObj and expanded in API
    // if ((hoh.include_asset_planning ?? true)) {
    //   const hohAssets = hoh.assets ?? [];
    //   if (hohAssets.length === 0) {
    //     return false;
    //   }
    // }

    // Check spouse assets if they opted in
    // if (spouse && (spouse.include_asset_planning ?? true)) {
    //   const spouseAssets = spouse.assets ?? [];
    //   if (spouseAssets.length === 0) {
    //     return false;
    //   }
    // }

    // Check other household members
    // for (const member of members) {
    //   if (member.role !== 'SPOUSE' && member.role_target_user_data) {
    //     if (member.role_target_user_data.include_asset_planning ?? true) {
    //       const memberAssets = member.role_target_user_data.assets ?? [];
    //       if (memberAssets.length === 0) {
    //         return false;
    //       }
    //     }
    //   }
    // }
  }

  // All required documents are complete
  return true;
};
